import { getItems, createItem, updateItem, fromDateOnly } from '../api/figApi';
import { buildProjectTree, emptyTree } from './projects';

// ============================================================
// THE WATCH — SharePoint read path.
//
// Raw Watch_Open / Watch_Sources / Watch_Meta rows in, items in the
// exact locked Watch field contract out. Watch.js must not be able to
// tell it is not reading the seed.
//
// Row-shape and absence rules come from the migration write-path
// probe — do not "improve" them:
//   - a row may arrive as { fields: {...} } or as the fields object
//     itself (getItems flattens; other callers may not)
//   - absent text → '', absent number → null, absent date → ''
//   - date columns are never Date-parsed, only fromDateOnly-sliced
//   - Triaged absent vs false are DIFFERENT states (never triaged vs
//     parked back) and the NEW rail depends on the key being absent
//   - heatOverride / parkCount / now / archivedAt / checklist follow
//     the same absent-by-default norm: no key unless the row has one
// ============================================================

const SWEEP_ENDPOINT = '/api/watch-sweep';

// Omit empty values entirely — '' and null read back as absent anyway
// (Runbook §3), and omitting keeps the payload honest. 0 is kept.
function dropEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

// Empty/absent input falls back silently; only malformed content warns.
function safeJson(raw, fallback, watchId) {
  if (raw === undefined || raw === null || raw === '') return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    console.warn(`[watchApi] unparseable JSON on WatchId ${watchId}:`, raw);
    return fallback;
  }
}

function toOpenItem(row) {
  const f = row.fields ?? row;

  const item = {
    id: f.WatchId ?? null,
    title: f.Title ?? '',
    kind: f.Kind ?? '',
    domain: f.Domain ?? '',
    status: f.Status ?? '',
    court: f.Court ?? '',
    counterparty: f.Counterparty ?? '',
    counterpartyEmail: f.CounterpartyEmail ?? '',   // the sweep's routing key; searchable, never written from here
    lastTouched: fromDateOnly(f.LastTouched) ?? '',
    coldDays: f.ColdDays ?? null,
    nextAction: f.NextAction ?? '',
    dueDate: fromDateOnly(f.DueDate) ?? '',
    wakeDate: fromDateOnly(f.WakeDate) ?? '',
    wakeAfter: f.WakeAfter ?? null,
    insuredName: f.InsuredName ?? '',
    person: f.Person ?? '',
    anchorId: f.AnchorId ?? null,
    confidence: f.Confidence ?? '',
    project: f.Project ?? '',
    projectId: f.ProjectId ?? '',                  // Watch_Projects.ProjectId, plain text; '' = unassigned
    tags: (f.Tags || '').split('|').filter(Boolean),
    links: safeJson(f.LinksJson, [], f.WatchId),
    notes: f.Notes ?? '',
    sources: [],
  };

  // SharePoint item id, kept for updateItem later. WatchId is the
  // board identity; spId is the row address.
  item.spId = row.id ?? f.id;

  if (f.Triaged !== undefined) item.triaged = !!f.Triaged;
  // Lock flags follow the Triaged norm: absent stays absent (never placed),
  // false is a real value (placed by a guess), true is the operator's word.
  if (f.ProjectLocked !== undefined && f.ProjectLocked !== null) item.projectLocked = !!f.ProjectLocked;
  if (f.TitleLocked !== undefined && f.TitleLocked !== null) item.titleLocked = !!f.TitleLocked;
  if (f.HeatOverride != null) item.heatOverride = f.HeatOverride;
  if (f.ParkCount != null) item.parkCount = f.ParkCount;
  item.pinned = f.Pinned === true;                 // D3: holds a NOW slot; NowSlot is no longer read
  if (f.ArchivedAt != null) item.archivedAt = fromDateOnly(f.ArchivedAt) ?? '';
  if (f.Checklist) item.checklist = safeJson(f.Checklist, [], f.WatchId);
  item.synopsis = parseSynopsis(f);

  return item;
}

// D1.5 — the sweep-owned status block. Synopsis is three \n-separated lines
// (standing / move / next); the move line arrives prefixed "My move: " or
// "Their move: " and the next line as "Check back YYYY-MM-DD" or "No
// check-back date" — those prefixes are the server's, stripped here so the
// header owns the wording. null when the card has no synopsis columns yet.
export function parseSynopsis(f) {
  const raw = typeof f.Synopsis === 'string' ? f.Synopsis : '';
  const at = f.SynopsisAt ?? '';
  const confidence = typeof f.SynopsisConfidence === 'number' ? f.SynopsisConfidence : null;
  if (!raw.trim() && confidence === null && !at) return null;
  const lines = raw.split('\n').map(l => l.trim());
  const standing = lines[0] || '';
  const move = (lines[1] || '').replace(/^(my|their|your)\s+move:\s*/i, '').replace(/^—$/, '');
  const nextLine = lines[2] || '';
  const m = /(\d{4}-\d{2}-\d{2})/.exec(nextLine);
  return {
    standing,
    move,
    nextCheck: fromDateOnly(f.SynopsisNextDate) || (m ? m[1] : ''),
    court: f.SynopsisCourt ?? '',
    confidence,
    at,
  };
}

// `spId` is the row address for a MOVE (CHECK rail): a flattened row carries
// it as f.id; a nested { id, fields } row hands it in from the caller.
function toSource(f, spId = f.id ?? null) {
  return {
    spId,
    routedBy: f.RoutedBy ?? '',                      // provenance stamp; '' on rows written before the column
    kind: f.SourceKind ?? '',
    date: fromDateOnly(f.SourceDate) ?? '',
    who: f.Who ?? '',
    org: f.Org ?? '',
    requestedBy: f.RequestedBy ?? '',
    requestedByOrg: f.RequestedByOrg ?? '',
    subject: f.Subject ?? '',
    why: f.Why ?? '',
    mailbox: f.Mailbox ?? '',
    conversationId: f.ConversationId ?? '',
    internetMessageId: f.InternetMessageId ?? '',
    messageId: f.MessageId ?? '',
    webLink: f.WebLink ?? '',
    isReplyTarget: !!f.IsReplyTarget,
    isReAsk: !!f.IsReAsk,
    // D1 intake — written by the sweep's extractor, absent on older rows and
    // on every row until the columns are provisioned. Absent number → null.
    direction: f.Direction === 'inbound' || f.Direction === 'outbound' ? f.Direction : '',
    ask: f.Ask ?? '',
    deadline: fromDateOnly(f.Deadline) ?? '',
    lane: f.Lane ?? '',
    summary: f.Summary ?? '',
    extractConfidence: f.ExtractConfidence ?? null,
    attachSuggested: f.AttachSuggested ?? null,
  };
}

// The item's intake reading: the single highest-confidence extraction among
// its sources (latest date breaks a tie). null when no source carries one —
// the NEW rail then falls back to the cleaned subject and shows no chips.
export function deriveIntake(sources) {
  let best = null;
  for (const s of sources || []) {
    if (typeof s.extractConfidence !== 'number') continue;
    if (!best || s.extractConfidence > best.extractConfidence
        || (s.extractConfidence === best.extractConfidence && (s.date || '') > (best.date || ''))) best = s;
  }
  if (!best) return null;
  return {
    summary: best.summary || '',
    ask: best.ask || '',
    deadline: best.deadline || '',
    lane: best.lane || '',
    confidence: best.extractConfidence,
    attachSuggested: best.attachSuggested ?? null,
  };
}

export function assembleWatch(openRows, sourceRows, metaRows) {
  const items = (openRows ?? []).map(toOpenItem).sort((a, b) => a.id - b.id);

  const byWatchId = new Map(items.map(item => [item.id, item]));
  let orphans = 0;
  for (const row of sourceRows ?? []) {
    const f = row.fields ?? row;
    const parent = byWatchId.get(f.WatchId);
    if (!parent) {
      orphans += 1;
      continue;
    }
    parent.sources.push(toSource(f, row.id ?? f.id ?? null));
  }
  if (orphans > 0) {
    console.warn(`[watchApi] dropped ${orphans} source(s) with no matching Watch_Open item`);
  }
  for (const item of items) item.intake = deriveIntake(item.sources);

  let sweptAt = null;
  for (const row of metaRows ?? []) {
    const f = row.fields ?? row;
    if (f.Title === 'WATCH_SWEPT_AT') sweptAt = f.Value ?? null;
  }

  return { items, sweptAt, lastReport: parseLastReport(metaRows) };
}

// The persisted last sweep report (Watch_Meta SWEEP_LAST_REPORT, written by
// the App Service on every WRITE run, success or failure; dry runs go to
// SWEEP_LAST_DRY, which the board never reads). null when the row is missing
// or its Value is not JSON — the header then shows the watermark alone and
// no dot.
export const LAST_REPORT_KEY = 'SWEEP_LAST_REPORT';
export function parseLastReport(metaRows) {
  for (const row of metaRows ?? []) {
    const f = row.fields ?? row;
    if (f.Title !== LAST_REPORT_KEY) continue;
    if (typeof f.Value !== 'string' || !f.Value) return null;
    try {
      const parsed = JSON.parse(f.Value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

// CHECK NOW: one Watch_Meta read, the parsed report only. Never runs a sweep.
export async function loadLastReport(token) {
  return parseLastReport(await getItems(token, 'Watch_Meta'));
}

const PARK_ENDPOINT = '/api/watch-park';

// PARK, made real (D1). Goes through the Netlify relay to the App Service
// /watch/park route, which writes Court Holding + Triaged true + ParkCount+1
// + WakeDate today+7 in one PATCH. The item leaves the NEW rail because
// Triaged flips true. Only a literal dryRun:false writes; this always sends it.
// opts.keepalive: true when the call is a flush from beforeunload.
// Phase C: the row's projectId rides along when it has one; the route then
// adds ProjectId + ProjectLocked:true to the same PATCH (parking with the
// chip in place is accepting it). No project → nothing project-related.
export async function parkItem(getIdToken, item, opts = {}) {
  const payload = { watchId: item.id, dryRun: false };
  if (typeof item.projectId === 'string' && /^PRJ-\d{5}$/.test(item.projectId)) payload.projectId = item.projectId;
  const doFetch = async (forceRefresh) => fetch(PARK_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken(forceRefresh)}` },
    body: JSON.stringify(payload),
    ...(opts.keepalive === true ? { keepalive: true } : {}),
  });
  let res = await doFetch(false);
  if (res.status === 401) res = await doFetch(true);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.upstreamStatus ? ` (upstream ${err.upstreamStatus})` : '';
    throw new Error((err.error || `park failed (${res.status})`) + detail);
  }
  const r = await res.json();
  const f = r.fields || {};
  return {
    ...item,
    court: f.Court || 'Holding',
    triaged: true,
    parkCount: typeof r.parkCount === 'number' ? r.parkCount : (Number(item.parkCount) || 0) + 1,
    wakeDate: f.WakeDate || item.wakeDate || '',
    ...(f.ProjectLocked === true || payload.projectId ? { projectLocked: true } : {}),
  };
}

// Disposition a NEW-rail item. Triaged is only ever written true here —
// never false (absent-vs-false is the NEW rail invariant). accept and kill
// PATCH Watch_Open directly; park goes through parkItem (the relay) and
// needs the ID-token getter in `opts`. Returns the updated item through the
// same row→item mapper assembleWatch uses. NOTE: the mapper starts sources
// at [] — the caller keeps the item's existing sources.
export async function dispositionItem(token, item, action, { getIdToken, payload, keepalive = false } = {}) {
  if (action === 'park') {
    if (typeof getIdToken !== 'function') throw new Error('[watchApi] park needs getIdToken');
    return parkItem(getIdToken, item, { keepalive });
  }
  let fields;
  if (action === 'accept') fields = acceptFields(item, payload);
  else if (action === 'kill') fields = { Triaged: true, Status: 'Killed' };
  else throw new Error(`[watchApi] unknown disposition action: ${action}`);
  const updated = keepalive === true
    ? await updateItem(token, 'Watch_Open', item.spId, fields, { keepalive: true })
    : await updateItem(token, 'Watch_Open', item.spId, fields);
  return toOpenItem(updated);
}

// ACCEPT with the inline strip's answers. Court only when chosen; the note
// is APPENDED to Notes with today's date, never replacing what is there; the
// date goes to DueDate when the court is Mine (it is Marc's deadline) and to
// WakeDate otherwise (it is when to check on them).
// Phase C: when the row carries a projectId, accepting is placement —
// ProjectId + ProjectLocked:true. With none, nothing project-related is
// written, so the lock rule holds: a lock only ever travels with a project.
export function acceptFields(item, payload = {}, today = new Date()) {
  const fields = { Triaged: true };
  if (typeof item?.projectId === 'string' && /^PRJ-\d{5}$/.test(item.projectId)) { fields.ProjectId = item.projectId; fields.ProjectLocked = true; }
  const court = payload?.court;
  if (court === 'Mine' || court === 'Theirs') fields.Court = court;
  const note = (payload?.note || '').trim();
  if (note) {
    const stamp = today.toISOString().slice(0, 10);
    const prev = (item?.notes || '').trim();
    fields.Notes = prev ? `${prev}\n[${stamp}] ${note}` : `[${stamp}] ${note}`;
  }
  const date = (payload?.date || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const effectiveCourt = court || item?.court;
    if (effectiveCourt === 'Mine') fields.DueDate = date; else fields.WakeDate = date;
  }
  return fields;
}

// PIN (D3): Pinned alone. The column is declared in figApi's Watch_Open
// definition, so the first access creates it when missing.
export async function setPinned(token, item, pinned) {
  const updated = await updateItem(token, 'Watch_Open', item.spId, { Pinned: pinned === true });
  return toOpenItem(updated);
}

// The "suggests: Theirs" hint on a card: set Court alone.
export async function setCourt(token, item, court) {
  if (!['Mine', 'Theirs', 'Holding'].includes(court)) throw new Error(`[watchApi] bad court: ${court}`);
  const updated = await updateItem(token, 'Watch_Open', item.spId, { Court: court });
  return toOpenItem(updated);
}

// The extractor's six columns, as the sweep writes them. Included when the
// message carries an extraction; a write that fails on them is retried once
// without them (the columns may not be provisioned yet), same as the server.
const INTAKE_COLUMNS = ['Ask', 'Deadline', 'Lane', 'Summary', 'ExtractConfidence', 'AttachSuggested', 'RoutedBy'];
function intakeFields(ex, suggest) {
  const f = {};
  if (ex) {
    if (ex.ask) f.Ask = ex.ask;
    if (ex.deadline) f.Deadline = ex.deadline;
    if (ex.lane) f.Lane = ex.lane;
    if (ex.summary) f.Summary = ex.summary;
    if (typeof ex.confidence === 'number') f.ExtractConfidence = ex.confidence;
  }
  if (suggest) f.AttachSuggested = suggest;
  return f;
}
let intakeColsMissing = false;
async function createSourceTolerant(token, fields) {
  const has = INTAKE_COLUMNS.some(k => k in fields);
  const strip = () => { const o = { ...fields }; for (const k of INTAKE_COLUMNS) delete o[k]; return o; };
  if (!has || intakeColsMissing) return createItem(token, 'Watch_Sources', has ? strip() : fields);
  try { return await createItem(token, 'Watch_Sources', fields); }
  catch (e) {
    intakeColsMissing = true;
    console.warn('[watchApi] intake columns missing on Watch_Sources — writing without them');
    return createItem(token, 'Watch_Sources', strip());
  }
}

// Merge a NEW card into an existing one (the "→ card #N" chip): copy its
// sources onto the target, then close it as Triaged. Closed, not Killed —
// the sweep's kill memory counts Killed cards per sender, and a merge is
// not a verdict on the sender.
export async function mergeIntoCard(token, item, targetId, today = new Date()) {
  const created = [];
  for (const s of item.sources || []) {
    const fields = dropEmpty({
      Title: s.subject, WatchId: targetId, SourceKind: s.kind || 'email', SourceDate: s.date, RoutedBy: 'merge',
      Who: s.who, Org: s.org, Subject: s.subject, Why: s.why, Mailbox: s.mailbox,
      ConversationId: s.conversationId, InternetMessageId: s.internetMessageId, MessageId: s.messageId,
      WebLink: (s.webLink || '').slice(0, 255),
      ...intakeFields({ ask: s.ask, deadline: s.deadline, lane: s.lane, summary: s.summary, confidence: s.extractConfidence }, 0),
    });
    created.push(toSource(await createSourceTolerant(token, fields)));
  }
  // The receipt: a dated "merged into #N" line appended to Notes, so the
  // closed card says where its thread went.
  const stamp = today.toISOString().slice(0, 10);
  const prev = (item?.notes || '').trim();
  const note = `[${stamp}] merged into #${targetId}`;
  const updated = await updateItem(token, 'Watch_Open', item.spId, { Triaged: true, Status: 'Closed', Notes: prev ? `${prev}\n${note}` : note });
  return { item: toOpenItem(updated), sources: created };
}

// MOVE source rows to another card (the CHECK rail): WatchId + RoutedBy
// 'attach' on each row, nothing else. `sources` are the item's mapped
// sources (they carry spId). Returns the moved sources re-mapped.
export async function moveSources(token, sources, targetWatchId) {
  const target = Number(targetWatchId);
  if (!Number.isInteger(target) || target <= 0) throw new Error('[watchApi] bad target WatchId');
  const moved = [];
  for (const s of sources || []) {
    if (s?.spId == null) throw new Error('[watchApi] a source row without an id cannot be moved');
    moved.push(toSource(await updateItem(token, 'Watch_Sources', s.spId, { WatchId: target, RoutedBy: 'attach' })));
  }
  return moved;
}

// ── Phase B: project edits ─────────────────────────────────────────────
// Every write here names exactly its fields and nothing else. None of them
// touch WatchId, Watch_Sources, Court, Pinned, Synopsis*, Status or
// LastTouched.

// MOVE — the card's ProjectId, locked so the sweep never re-files it. A blank
// projectId is Marc choosing "no project": the column is CLEARED (null, not
// '' — see contactsApi's clearing rule) and the lock still lands.
export async function moveCard(token, item, projectId) {
  const fields = { ProjectId: projectId ? String(projectId) : null, ProjectLocked: true };
  const updated = await updateItem(token, 'Watch_Open', item.spId, fields);
  return toOpenItem(updated);
}

// RENAME a card — Title, locked so the sweep's human-title pass leaves it.
export async function renameCard(token, item, title) {
  const t = String(title || '').trim();
  if (!t) throw new Error('[watchApi] a card needs a title');
  const updated = await updateItem(token, 'Watch_Open', item.spId, { Title: t, TitleLocked: true });
  return toOpenItem(updated);
}

// RENAME a project — Name and the built-in Title (the list view's caption).
// Cards carry the id, so nothing else moves.
export async function renameProject(token, project, name) {
  const n = String(name || '').trim();
  if (!n) throw new Error('[watchApi] a project needs a name');
  const updated = await updateItem(token, 'Watch_Projects', project.spId, { Name: n, Title: n });
  return toProject(updated);
}

// ARCHIVE a project — Status alone. The row stays (its id may still be on
// cards); the tree drops it on the next load. This is the undo for CREATE.
export async function archiveProject(token, project) {
  const updated = await updateItem(token, 'Watch_Projects', project.spId, { Status: 'Archived' });
  return toProject(updated);
}

// The next PRJ-NNNNN over a set of project rows (max + 1, zero-padded).
export function nextIdOver(projects) {
  let max = 0;
  for (const p of projects || []) { const m = /^PRJ-(\d+)$/.exec(p?.id || ''); if (m) max = Math.max(max, Number(m[1])); }
  return `PRJ-${String(max + 1).padStart(5, '0')}`;
}
const isDuplicateIdError = (e) => /duplicate|unique|already exists|SPDuplicateValues/i.test(String(e?.message || ''));

// CREATE a project. The id is max + 1 over a FRESH read of Watch_Projects
// (archived rows included — an archived id is still taken), SortOrder is
// last + 1 among its siblings, depth is capped at 3. ProjectId is
// unique-constrained on the server: a duplicate (a racing tab) re-reads and
// retries once, then throws.
export async function createProject(token, { name, parentId = '', color = '', sortOrder = null, type = 'general' }) {
  const n = String(name || '').trim();
  if (!n) throw new Error('[watchApi] a project needs a name');
  const attempt = async () => {
    const rows = (await getItems(token, 'Watch_Projects')).map(toProject);
    const byId = new Map(rows.map(r => [r.id, r]));
    let depth = 1;
    for (let p = parentId ? byId.get(parentId) : null; p; p = p.parentId ? byId.get(p.parentId) : null) depth++;
    if (parentId && !byId.has(parentId)) throw new Error(`[watchApi] parent ${parentId} does not exist`);
    if (depth > 3) throw new Error('[watchApi] the tree allows three levels');
    const siblings = rows.filter(r => (r.parentId || '') === (parentId || '') && r.status !== 'Archived');
    const order = sortOrder != null ? Number(sortOrder) : Math.max(0, ...siblings.map(r => Number(r.sortOrder) || 0)) + 1;
    const fields = {
      Title: n, ProjectId: nextIdOver(rows), Name: n, Color: color || '', SortOrder: order,
      Status: 'Active', ProjectType: type || 'general',
    };
    if (parentId) fields.ParentProjectId = parentId;
    return toProject(await createItem(token, 'Watch_Projects', fields));
  };
  try { return await attempt(); }
  catch (e) {
    if (!isDuplicateIdError(e)) throw e;
    return attempt();
  }
}

// ── Projects (Watch_Projects) ──────────────────────────────────────────

// One Watch_Projects row → the node shape projects.js builds the tree from.
// spId is the row address for later PATCHes (rename, recolor, move).
export function toProject(row) {
  const f = row.fields ?? row;
  return {
    id: f.ProjectId ?? '',
    name: f.Name || f.Title || '',
    parentId: f.ParentProjectId ?? '',
    color: f.Color ?? '',
    sortOrder: f.SortOrder ?? null,
    status: f.Status || 'Active',
    type: f.ProjectType || 'general',
    policyRef: f.PolicyRef ?? '',
    insuredId: f.InsuredId ?? '',
    spId: row.id ?? f.id,
  };
}

// The tree, or an empty one plus the reason when the list is malformed
// (depth 4, cycle, missing parent) — the board must still render; the
// masthead shows the message. Archived projects are left out of the tree.
export function assembleProjects(projectRows) {
  const projects = (projectRows ?? []).map(toProject).filter(p => p.id && p.status !== 'Archived');
  try {
    return { tree: buildProjectTree(projects), list: projects, error: null };
  } catch (e) {
    console.warn('[watchApi] Watch_Projects unusable:', e.message);
    return { tree: emptyTree(), list: [], error: e.message };
  }
}

export async function loadProjects(token) {
  return assembleProjects(await getItems(token, 'Watch_Projects'));
}

export async function loadWatch(token) {
  const [openRows, sourceRows, metaRows, projectRows] = await Promise.all([
    getItems(token, 'Watch_Open'),
    getItems(token, 'Watch_Sources'),
    getItems(token, 'Watch_Meta'),
    getItems(token, 'Watch_Projects'),
  ]);
  const { tree, list, error } = assembleProjects(projectRows);
  return { ...assembleWatch(openRows, sourceRows, metaRows), projects: tree, projectList: list, projectsError: error };
}

// ── Stage 2: the sweep and its three verbs ─────────────────────────────

// Calls the Netlify relay, which verifies the Microsoft ID token and
// forwards to the App Service /sweep route. Takes a token GETTER so a
// stale cached ID token can be refreshed once on 401 (same pattern as
// jarvisApi). Only literal dryRun:false opens the write path.
//
// `exclude` is an array of BARE thread keys (proposed[].key, no rail prefix).
// The server routes a listed thread to unmatched with reason 'dismissed by
// operator' instead of proposing it, so a dismissed proposal is not written.
// Filter first, then test: a caller handing over ['', null] omits the key
// rather than sending junk. Same contract as `insured` — absent, never empty.
export async function runSweep(getIdToken, { dryRun = true, insured, exclude } = {}) {
  const payload = { dryRun: dryRun === false ? false : true };
  if (typeof insured === 'string' && insured.trim()) payload.insured = insured.trim();
  const excludeKeys = Array.isArray(exclude) ? exclude.filter(k => typeof k === 'string' && k) : [];
  if (excludeKeys.length) payload.exclude = [...new Set(excludeKeys)];
  const doFetch = async (forceRefresh) => fetch(SWEEP_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken(forceRefresh)}` },
    body: JSON.stringify(payload),
  });
  let res = await doFetch(false);
  if (res.status === 401) res = await doFetch(true);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.upstreamStatus ? ` (upstream ${err.upstreamStatus})` : '';
    throw new Error((err.error || `sweep failed (${res.status})`) + detail);
  }
  return res.json();
}

// ACCEPT COURT — writes Court, plus whatever of counterparty / next action /
// wake the user filled in. LastTouched is the sweep's to write.
export async function acceptCourt(token, item, court, extra = {}) {
  if (!['Mine', 'Theirs', 'Holding'].includes(court)) throw new Error(`[watchApi] bad court: ${court}`);
  const fields = { Court: court };
  if (extra.counterparty) fields.Counterparty = extra.counterparty;
  if (extra.nextAction) fields.NextAction = extra.nextAction;
  if (extra.wakeDate) fields.WakeDate = extra.wakeDate; // 'YYYY-MM-DD'; updateItem's dateOnly guard handles it
  const updated = await updateItem(token, 'Watch_Open', item.spId, fields);
  return toOpenItem(updated);
}

// ATTACH TO … — one Watch_Sources row per message in a proposed thread,
// field-for-field what the sweep's own write path would have written, so
// the next write-sweep recognises them by identity key
// (WatchId|ConversationId|SourceDate|InternetMessageId) and does not
// create a duplicate Open. `msgs` is the `msgs` array off a proposed row.
// `body` is the server's cleaned full text (watch-body.js, capped at 6,000);
// `preview` is the 255-char Graph preview kept for pre-D1 reports. No client
// cap on Why any more — the column is multi-line and uncapped.
export async function attachSources(token, watchId, msgs, mailbox) {
  const created = [];
  for (const m of msgs) {
    const text = (m.body || m.preview || '').trim();
    const fields = dropEmpty({
      Title: m.subject,
      WatchId: watchId,
      SourceKind: 'email',
      RoutedBy: 'attach',
      SourceDate: m.ct?.date,
      Who: m.fromMe ? (m.toName || (m.to || [])[0] || '') : (m.fromName || m.from),
      Org: ((m.fromMe ? ((m.to || [])[0] || '') : (m.from || '')).split('@')[1]) || '',
      Subject: m.subject,
      Mailbox: mailbox,
      ConversationId: m.conversationId,
      InternetMessageId: m.internetMessageId,
      MessageId: m.id,
      WebLink: (m.webLink || '').slice(0, 255),
      Why: [text, m.attachments?.length ? `Attachments: ${m.attachments.join('; ')}` : ''].filter(Boolean).join(' · '),
      ...intakeFields(m.extraction, 0),
    });
    created.push(await createSourceTolerant(token, fields));
  }
  return created.map(toSource);
}
